import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  MagnifyingGlassIcon,
  StarIcon,
  GlobeAltIcon,
  LockClosedIcon,
  TrashIcon,
  PencilIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import type { SavedView } from '../types';
import { SavedViewRow } from './SavedViewRow';

interface SavedViewDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement>;
  views: SavedView[];
  defaultViewId: number | null;
  activeViewId: number | null;
  currentUserEmail: string | null;
  onSelectView: (view: SavedView) => void;
  onSetDefault: (viewId: number | null) => void;
  onTogglePublic: (view: SavedView) => void;
  onEditView: (view: SavedView) => void;
  onDeleteView: (view: SavedView) => void;
  onCopyLink: (view: SavedView) => void;
}

export const SavedViewDropdown: React.FC<SavedViewDropdownProps> = ({
  isOpen,
  onClose,
  anchorRef,
  views,
  defaultViewId,
  activeViewId,
  currentUserEmail,
  onSelectView,
  onSetDefault,
  onTogglePublic,
  onEditView,
  onDeleteView,
  onCopyLink,
}) => {
  const [search, setSearch] = useState('');
  const [menuOpenId, setMenuOpenId] = useState<number | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);
  const [descTooltip, setDescTooltip] = useState<{ text: string; top: number; left: number } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, right: 0 });
  // Mirror menuOpenId into a ref so the outside-click / Esc effect below can
  // read the latest value without listing menuOpenId in its deps — otherwise
  // the effect resubscribes on every action-menu open/close.
  const menuOpenIdRef = useRef<number | null>(null);
  useEffect(() => {
    menuOpenIdRef.current = menuOpenId;
  }, [menuOpenId]);

  useEffect(() => {
    if (isOpen && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
      setSearch('');
      setMenuOpenId(null);
    }
  }, [isOpen, anchorRef]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const inDropdown = dropdownRef.current?.contains(target);
      const inActionMenu = actionMenuRef.current?.contains(target);
      if (!inDropdown && !inActionMenu) {
        onClose();
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (menuOpenIdRef.current !== null) {
          setMenuOpenId(null);
          setMenuPosition(null);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  // Close action menu when main dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setMenuOpenId(null);
      setMenuPosition(null);
    }
  }, [isOpen]);

  const { systemViews, myViews, teamViews } = useMemo(() => {
    const q = search.toLowerCase().trim();
    const filteredViews = q
      ? views.filter((v) => v.name.toLowerCase().includes(q))
      : views;
    return {
      systemViews: filteredViews.filter((v) => v.is_system),
      myViews: filteredViews.filter(
        (v) => !v.is_system && v.author_email === currentUserEmail
      ),
      teamViews: filteredViews.filter(
        (v) => !v.is_system && v.is_public && v.author_email !== currentUserEmail
      ),
    };
  }, [views, search, currentUserEmail]);

  // Stable tooltip handlers — prevents per-row closure allocation on each
  // render, which matters when the list has many views.
  const showTooltip = useCallback(
    (text: string, rect: DOMRect) => {
      setDescTooltip({
        text,
        top: rect.bottom + 6,
        left: rect.left + rect.width / 2,
      });
    },
    []
  );
  const hideTooltip = useCallback(() => setDescTooltip(null), []);
  const handleTooltipEnter = useCallback(
    (e: React.MouseEvent<Element> | React.FocusEvent<Element>) => {
      const text = e.currentTarget.getAttribute('data-tooltip');
      if (!text) return;
      showTooltip(text, e.currentTarget.getBoundingClientRect());
    },
    [showTooltip]
  );

  // Stable row callbacks: read menuOpenIdRef.current instead of closing over
  // menuOpenId, so SavedViewRow (memoized) doesn't lose reference equality
  // every time the action menu opens/closes.
  const handleRowSelect = useCallback(
    (view: SavedView) => {
      // Any open action menu should dismiss on a row click rather than
      // silently eat the click. We don't also select the view — the user's
      // intent is ambiguous (dismiss vs. pick), so dismiss is the safer
      // default; the next click will select.
      if (menuOpenIdRef.current !== null) {
        setMenuOpenId(null);
        setMenuPosition(null);
        return;
      }
      onSelectView(view);
      onClose();
    },
    [onSelectView, onClose]
  );

  const handleRowOpenMenu = useCallback((view: SavedView, rect: DOMRect) => {
    if (menuOpenIdRef.current === view.id) {
      setMenuOpenId(null);
      setMenuPosition(null);
      return;
    }
    setMenuOpenId(view.id);
    setMenuPosition({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
  }, []);

  if (!isOpen) return null;

  const isOwner = (v: SavedView) => v.author_email === currentUserEmail;

  const activeMenuView = menuOpenId != null ? views.find((v) => v.id === menuOpenId) : undefined;
  const renderActionMenu = () => {
    if (!activeMenuView || !menuPosition) return null;
    const view = activeMenuView;
    const isDefault = view.id === defaultViewId;
    const closeMenu = () => {
      setMenuOpenId(null);
      setMenuPosition(null);
    };
    return createPortal(
      <div
        ref={actionMenuRef}
        className="fixed w-44 bg-white border border-gray-200 rounded-md shadow-lg z-[10001] py-1"
        style={{ top: menuPosition.top, right: menuPosition.right }}
        onClick={(e) => e.stopPropagation()}
      >
        {isOwner(view) && !view.is_system && (
          <button
            className="flex items-center gap-2 w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            onClick={() => {
              onEditView(view);
              closeMenu();
              onClose();
            }}
          >
            <PencilIcon className="h-3.5 w-3.5" />
            Edit
          </button>
        )}
        {isOwner(view) && !view.is_system && (
          <button
            className="flex items-center gap-2 w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            onClick={() => {
              onTogglePublic(view);
              closeMenu();
            }}
          >
            {view.is_public ? (
              <>
                <LockClosedIcon className="h-3.5 w-3.5" />
                Make Private
              </>
            ) : (
              <>
                <GlobeAltIcon className="h-3.5 w-3.5" />
                Make Public
              </>
            )}
          </button>
        )}
        <button
          className="flex items-center gap-2 w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          onClick={() => {
            onSetDefault(isDefault ? null : view.id);
            closeMenu();
          }}
        >
          {isDefault ? (
            <>
              <StarIcon className="h-3.5 w-3.5" />
              Unset Default
            </>
          ) : (
            <>
              <StarIconSolid className="h-3.5 w-3.5 text-amber-500" />
              Set as Default
            </>
          )}
        </button>
        <button
          className="flex items-center gap-2 w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          onClick={() => {
            onCopyLink(view);
            closeMenu();
          }}
        >
          <LinkIcon className="h-3.5 w-3.5" />
          Copy Link
        </button>
        {isOwner(view) && !view.is_system && (
          <>
            <div className="border-t border-gray-200 my-1" />
            <button
              className="flex items-center gap-2 w-full px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-100 hover:text-red-700"
              onClick={() => {
                onDeleteView(view);
                closeMenu();
                onClose();
              }}
            >
              <TrashIcon className="h-3.5 w-3.5" />
              Delete
            </button>
          </>
        )}
      </div>,
      document.body
    );
  };

  const renderSection = (
    title: string,
    items: SavedView[],
    showAuthor = false,
    emptyMessage?: string
  ) => {
    if (items.length === 0 && !emptyMessage) return null;
    return (
      <div>
        <div className="px-3 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
          {title}
        </div>
        {items.length === 0 ? (
          <div className="px-3 py-1.5 text-xs text-gray-400 italic">{emptyMessage}</div>
        ) : (
          <div className="relative">
            {items.map((v) => (
              <SavedViewRow
                key={v.id}
                view={v}
                showAuthor={showAuthor}
                isActive={v.id === activeViewId}
                isDefault={v.id === defaultViewId}
                isOwnView={v.author_email === currentUserEmail}
                isMenuOpen={menuOpenId === v.id}
                onSelect={handleRowSelect}
                onOpenMenu={handleRowOpenMenu}
                onTooltipEnter={handleTooltipEnter}
                onTooltipLeave={hideTooltip}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {createPortal(
        <div
          ref={dropdownRef}
          role="menu"
          aria-label="Saved views"
          className="fixed bg-white border border-gray-200 rounded-lg shadow-xl w-96 max-h-[520px] flex flex-col z-[10000]"
          style={{ top: position.top, right: position.right }}
        >
          {/* Search */}
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <MagnifyingGlassIcon aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search views"
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search views..."
                autoFocus
              />
            </div>
          </div>

          {/* List — action menus are portaled out, so no overflow hack needed */}
          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            {search.trim() &&
            systemViews.length === 0 &&
            myViews.length === 0 &&
            teamViews.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-400 text-center">No views found</div>
            ) : (
              <>
                {renderSection('System', systemViews)}
                {renderSection('My Views', myViews)}
                {renderSection('Team Views', teamViews, true, 'No team views yet')}
              </>
            )}
          </div>
        </div>,
        document.body
      )}
      {renderActionMenu()}
      {descTooltip &&
        createPortal(
          <div
            role="tooltip"
            className="fixed z-[10002] max-w-xs px-3 py-2 text-xs text-white bg-gray-900 rounded-md shadow-lg pointer-events-none whitespace-pre-wrap"
            style={{
              top: descTooltip.top,
              left: descTooltip.left,
              transform: 'translateX(-50%)',
            }}
          >
            {descTooltip.text}
          </div>,
          document.body
        )}
    </>
  );
};
